import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { FileText, Truck, Calendar, ArrowRight } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { formatCurrency, formatDate, getStatusColor, getStatusLabel } from "../../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function CustomerDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ bookings: 0 });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [statsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers, withCredentials: true }),
        axios.get(`${API}/bookings`, { headers, withCredentials: true })
      ]);
      
      setStats(statsRes.data);
      setRecentBookings(bookingsRes.data.slice(0, 5));
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout title="Dashboard">
      <div className="space-y-6" data-testid="customer-dashboard">
        {/* Welcome */}
        <div className="bg-primary text-primary-foreground p-6">
          <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Benvenuto, {user?.name}!
          </h2>
          <p className="text-primary-foreground/80">
            Gestisci le tue prenotazioni e trova il furgone perfetto per le tue esigenze.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Le Mie Prenotazioni
              </CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono">{stats.bookings}</div>
            </CardContent>
          </Card>
          
          <Card className="md:col-span-2">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold mb-1">Trova il tuo prossimo furgone</h3>
                  <p className="text-sm text-muted-foreground">
                    Sfoglia il catalogo e prenota in pochi click
                  </p>
                </div>
                <Button onClick={() => navigate("/catalogo")} className="btn-primary py-2" data-testid="go-to-catalog">
                  Vai al Catalogo
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Bookings */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              Prenotazioni Recenti
            </CardTitle>
            <Button variant="ghost" onClick={() => navigate("/prenotazioni")} data-testid="view-all-bookings">
              Vedi Tutte
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
            ) : recentBookings.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Nessuna prenotazione</p>
                <Button variant="outline" className="mt-4" onClick={() => navigate("/catalogo")}>
                  Prenota Ora
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {recentBookings.map((booking) => (
                  <div
                    key={booking.booking_id}
                    className="flex items-center justify-between p-4 bg-muted/50 border border-border"
                  >
                    <div className="flex items-center gap-4">
                      <div className="bg-primary/10 p-2">
                        <Truck className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">Prenotazione #{booking.booking_id.slice(-8)}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(booking.start_date)} - {formatDate(booking.end_date)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-2 py-1 text-xs font-medium ${getStatusColor(booking.status)}`}>
                        {getStatusLabel(booking.status)}
                      </span>
                      <span className="font-mono font-bold">{formatCurrency(booking.total_price)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
